const { importProductData } = require('./import')
const { getConfigurationsByIds } = require('../../../configuration/api/read/get-by-ids')
const { codeToProductId } = require('../../tools/import-validation/content/utils')

const resolveRowId = row => (row._id ? String(row._id).toLowerCase() : codeToProductId(row.code))

const serviceEntryFromRow = (row, facilityKey, serviceId) => {
  const entry = { id: serviceId }
  const productLine = row[facilityKey.replace('.hasService', '.productLine')]
  const index = row[facilityKey.replace('.hasService', '.index')]
  if (productLine) entry.productLine = productLine
  if (index) entry.index = index
  return entry
}

const addProductIdForService = (productIdsByServiceId, serviceId, productId) => {
  const existing = productIdsByServiceId.get(serviceId) || new Set()
  existing.add(productId)
  productIdsByServiceId.set(serviceId, existing)
}

async function commitCreateProducts (state, createRows, context) {
  if (!createRows.length) {
    return { created: 0, configurationsUpdated: 0 }
  }

  const rowsForImport = createRows.map(row => ({ ...row }))
  const dryRunResult = await importProductData(state, rowsForImport, { dryRun: true })

  if (dryRunResult.update.length || dryRunResult.unchangedIds.length) {
    throw new Error('Unexpected error: a create row resolved to an existing product - content validation should have prevented this')
  }

  const newProductDocs = dryRunResult.new
  const rowsById = new Map(createRows.map(row => [resolveRowId(row), row]))

  const productIdsByServiceId = new Map()

  for (const doc of newProductDocs) {
    const row = rowsById.get(doc._id)
    const services = []

    for (const { facilityKeys, warehouseKey } of context.serviceColumnsByProgram.values()) {
      for (const key of facilityKeys) {
        if (row[key] !== 'YES') continue
        const serviceId = context.columnKeyToServiceId.get(key)
        if (!serviceId) continue
        services.push(serviceEntryFromRow(row, key, serviceId))
        addProductIdForService(productIdsByServiceId, serviceId, doc._id)
      }

      if (warehouseKey && row[warehouseKey] === 'YES') {
        const warehouseServiceId = context.columnKeyToServiceId.get(warehouseKey)
        if (warehouseServiceId) {
          addProductIdForService(productIdsByServiceId, warehouseServiceId, doc._id)
        }
      }
    }

    doc.services = services
  }

  const serviceIds = [...productIdsByServiceId.keys()]
  const configDocs = serviceIds.length
    ? await getConfigurationsByIds(state, serviceIds.map(id => `configuration:${id}`))
    : []

  if (configDocs.length !== serviceIds.length) {
    const foundServiceIds = new Set(configDocs.map(doc => doc.service))
    const missing = serviceIds.filter(id => !foundServiceIds.has(id))
    throw new Error(`Missing configuration doc(s) for service(s): ${missing.join(', ')} - cannot commit`)
  }

  const originalConfigDocsById = new Map(configDocs.map(doc => [doc._id, doc]))
  const username = state.user ? state.user.name : ''
  const updatedConfigDocs = configDocs.map(doc => {
    const newIds = [...(productIdsByServiceId.get(doc.service) || [])]
    return {
      ...doc,
      products: Array.from(new Set((doc.products || []).concat(newIds))),
      updatedAt: new Date().toISOString(),
      updatedBy: username
    }
  })

  const combinedDocs = [...newProductDocs, ...updatedConfigDocs]
  const response = await state.productsDB.bulkDocs(combinedDocs)

  const failures = response.filter(row => row.error)

  if (!failures.length) {
    return { created: newProductDocs.length, configurationsUpdated: updatedConfigDocs.length }
  }

  // Rollback - if there is any error in the saving process, rollback the entire save, do not save partially
  const compensatingDocs = []
  response.forEach((result, index) => {
    if (result.error) return
    const original = combinedDocs[index]
    if (index < newProductDocs.length) {
      compensatingDocs.push({ _id: original._id, _rev: result.rev, _deleted: true })
    } else {
      const before = originalConfigDocsById.get(original._id)
      compensatingDocs.push({ ...before, _rev: result.rev })
    }
  })

  if (compensatingDocs.length) {
    try {
      await state.productsDB.bulkDocs(compensatingDocs)
    } catch (rollbackErr) {
      console.error('CRITICAL: rollback after a partial commit-create failure itself failed - manual intervention needed', rollbackErr, compensatingDocs)
    }
  }

  const err = new Error('Commit failed - nothing was persisted')
  err.data = failures
  throw err
}

module.exports = { commitCreateProducts }
